(() => {
  const sliders = new WeakSet();
  const comparisonDialogs = new WeakSet();
  const sliderCleanups = new WeakMap();
  const comparisonDialogCleanups = new WeakMap();

  const setupSlider = (slider) => {
    if (sliders.has(slider)) return;
    const track = slider.querySelector('[data-products-track]');
    const previous = slider.querySelector('[data-products-previous]');
    const next = slider.querySelector('[data-products-next]');
    if (!track || !previous || !next) return;
    sliders.add(slider);
    const controller = new AbortController();
    const { signal } = controller;

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;
    let suppressClick = false;
    let activePointerId = null;

    const updateArrow = () => {
      const canScroll = track.scrollWidth > track.clientWidth + 1;
      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= maxScrollLeft - 2;

      previous.hidden = !canScroll || atStart;
      previous.disabled = !canScroll || atStart;
      next.hidden = !canScroll || atEnd;
      next.disabled = !canScroll || atEnd;
    };

    const scrollByCard = (direction) => {
      const firstCard = track.querySelector('.beambox-products__card');
      const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth * 0.8;
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      track.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
    };

    const onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!event.target.closest('[data-products-track]')) return;
      if (event.target.closest('.beambox-products__arrow')) return;
      dragging = true;
      moved = false;
      suppressClick = false;
      activePointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 6) {
        moved = true;
        track.classList.add('beambox-products__grid--dragging');
        slider.setPointerCapture?.(event.pointerId);
      }

      if (moved) {
        track.scrollLeft = startScrollLeft - delta;
        event.preventDefault();
      }
    };

    const endDrag = (event) => {
      if (!dragging) return;
      if (activePointerId !== null && event?.pointerId !== undefined && event.pointerId !== activePointerId) return;
      dragging = false;
      suppressClick = moved;
      track.classList.remove('beambox-products__grid--dragging');
      if (activePointerId !== null && slider.hasPointerCapture?.(activePointerId)) {
        slider.releasePointerCapture(activePointerId);
      }
      activePointerId = null;
      if (suppressClick) window.setTimeout(() => { suppressClick = false; }, 0);
    };

    track.addEventListener('scroll', updateArrow, { passive: true, signal });
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    slider.addEventListener('pointerdown', onPointerDown, { signal });
    slider.addEventListener('pointermove', onPointerMove, { passive: false, signal });
    slider.addEventListener('pointerup', endDrag, { signal });
    slider.addEventListener('pointercancel', endDrag, { signal });
    slider.addEventListener('lostpointercapture', endDrag, { signal });
    window.addEventListener('blur', endDrag, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) endDrag();
    }, { signal });
    track.addEventListener('click', (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }
    }, { capture: true, signal });
    previous.addEventListener('click', () => scrollByCard(-1), { signal });
    next.addEventListener('click', () => scrollByCard(1), { signal });

    const resizeObserver = new ResizeObserver(updateArrow);
    resizeObserver.observe(track);
    sliderCleanups.set(slider, () => {
      endDrag();
      resizeObserver.disconnect();
      controller.abort();
      sliders.delete(slider);
      sliderCleanups.delete(slider);
    });
    updateArrow();
  };

  const setupComparisonDialog = (dialog) => {
    if (comparisonDialogs.has(dialog)) return;
    comparisonDialogs.add(dialog);
    const controller = new AbortController();
    const { signal } = controller;

    const dialogId = dialog.id;
    const close = () => {
      if (dialog.open) dialog.close();
    };

    document.querySelectorAll('[data-series-comparison-open]').forEach((trigger) => {
      if (trigger.getAttribute('aria-controls') !== dialogId) return;
      trigger.addEventListener('click', () => {
        if (!dialog.open) dialog.showModal();
      }, { signal });
    });

    dialog.querySelector('[data-series-comparison-close]')?.addEventListener('click', close, { signal });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    }, { signal });
    comparisonDialogCleanups.set(dialog, () => {
      close();
      controller.abort();
      comparisonDialogs.delete(dialog);
      comparisonDialogCleanups.delete(dialog);
    });
  };

  const mount = (root = document) => {
    root.querySelectorAll?.('[data-products-slider]').forEach(setupSlider);
    root.querySelectorAll?.('[data-series-comparison-dialog]').forEach(setupComparisonDialog);
  };

  const unmount = (root) => {
    root.querySelectorAll?.('[data-products-slider]').forEach((slider) => sliderCleanups.get(slider)?.());
    root.querySelectorAll?.('[data-series-comparison-dialog]').forEach((dialog) => {
      comparisonDialogCleanups.get(dialog)?.();
    });
  };

  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
